import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Radio,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CustomTable from "global/components/CustomTable/CustomTable";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import {
  convertESTtoUserLocalDateAndTime,
  getABResultAfterType,
  getFormattedStatsCount,
  getTentativeResultTime,
  isTruthy,
} from "helpers/methods";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import {
  AbTestResult,
  CampaignCount,
  CampaignOverviewType,
} from "models/interfaces";
import { useState } from "react";
import {
  metricLabels,
  variableOptions,
} from "screens/Campaigns/CampaignConstants";
import {
  variables,
  winningMetric,
  deliveryOptions,
} from "screens/Campaigns/CreateCampaign/ABTestCampaign/Constant";
import history from "utils/history";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getStatusColor } from "screens/Campaigns/Components/EmailDataList";
import moment, { Moment } from "moment";
import SendABCampaign from "./SendABCampaign";

interface CustomProps {
  campaignData: CampaignOverviewType;
  setCampaignData: Function;
  childCampaign: CampaignOverviewType[];
  setChildCampaign: Function;
}

const ABTestDetails = (props: CustomProps) => {
  const theme = useTheme();
  const isManual =
    props?.campaignData?.abTestInfo?.winingExecutionType === "Manual";
  const [selectedCampaign, setSelectedCampaign] = useState<{
    id: number;
    scheduleTime: string;
  } | null>(null);
  const [schedule, setSchedule] = useState<boolean>(false);

  const handleSelectCampaign = (id: number, scheduleTime: string) => {
    setSelectedCampaign({ id, scheduleTime });
  };

  const isChildCampaignExecuted = (): boolean => {
    return (
      isTruthy(props.childCampaign) &&
      props.childCampaign.every(
        (childCampaign: CampaignOverviewType) =>
          childCampaign.campaign?.status === strings.Completed
      )
    );
  };

  const tableHeader = [
    ...(isManual &&
    props?.campaignData?.campaign?.status === strings.ABTestWaiting &&
    isChildCampaignExecuted()
      ? [{ name: "Choose", field: "choose" }]
      : []),
    {
      name:
        props.campaignData?.abTestInfo?.testingVariable !== undefined
          ? variableOptions[props.campaignData.abTestInfo.testingVariable].label
          : "Subject Line",
      field: "testingVariable",
    },
    {
      name: "Status",
      field: "status",
    },
    {
      name: "Requested",
      field: "requested",
    },
    {
      name: "Success",
      field: "success",
    },
    {
      name: "Open Rate",
      field: "opens",
    },
    {
      name: "Click Rate",
      field: "clicks",
    },
  ];

  const getFilteredCounts = (counts: CampaignCount[]) => {
    return counts.reduce(
      (obj, item) =>
        Object.assign(obj, { [metricLabels[item.metricId]]: item.count }),
      {}
    );
  };

  const getTableRows = () => {
    return props.campaignData?.abTestInfo?.abTestResults.map(
      (testingValue: AbTestResult, index: number) => {
        const findChild = props.childCampaign.find(
          (child: CampaignOverviewType) =>
            child.campaign?.id === testingValue.campaignId
        );
        const status =
          findChild?.campaign?.status === "ABTestWaiting"
            ? "Preparing Groups"
            : findChild?.campaign?.status;   
        const clickable = status !== "Preparing Groups" && status !== "Submitted";
        const parentCampaignStatus = props?.campaignData?.campaign?.status;

        let childCountData: any = {};
        if (parentCampaignStatus !== strings.ABTestWaiting) {
          if (Array.isArray(findChild?.campaignCounts)) {
            childCountData = getFilteredCounts(findChild?.campaignCounts!);
          } else {
            childCountData = {};
          }
        }

        const requestedCount =
          parentCampaignStatus !== strings.ABTestWaiting
            ? childCountData?.Requested ?? 0
            : findChild?.campaignCounts.find((item) => item.metricId === 1)
                ?.count || 0;

        const successCount =
          parentCampaignStatus !== strings.ABTestWaiting
            ? childCountData?.Success ?? 0
            : findChild?.campaignCounts.find((item) => item.metricId === 3)
                ?.count || 0;

        const openedCount =
          parentCampaignStatus !== strings.ABTestWaiting
            ? childCountData?.Opened ?? 0
            : findChild?.campaignCounts.find((item) => item.metricId === 5)
                ?.count || 0;

        const clickedCount =
          parentCampaignStatus !== strings.ABTestWaiting
            ? childCountData?.Clicked ?? 0
            : findChild?.campaignCounts.find((item) => item.metricId === 6)
                ?.count || 0;

        return {
          choose: (
            <Radio
              checked={selectedCampaign?.id === findChild?.campaign?.id}
              onChange={() =>
                handleSelectCampaign(
                  findChild?.campaign?.id!,
                  props.campaignData?.abTestInfo?.testingVariable ===
                    "scheduleTime"
                    ? testingValue.testingValue
                    : findChild?.campaign?.scheduleTime!
                )
              }
            />
          ),
          testingVariable: (
            <Stack
              onClick={() => {
                clickable &&
                  history.push({
                    pathname: viewpaths.CampaignStatsViewPath,
                    search: `?id=${testingValue.campaignId}`,
                    state: {
                      parentCampaignType: props.campaignData.campaign?.type,
                    },
                  });
              }}
              direction="row"
              alignItems="center"
              sx={[
                clickable && {
                  cursor: "pointer",
                },
              ]}
            >
              {props.campaignData?.abTestInfo?.testingVariable ===
              "scheduleTime" ? (
                <Typography
                  variant="body2"
                  sx={[
                    {
                      display: "flex",
                      alignItems: "center",
                    },
                    clickable && {
                      cursor: "pointer",
                    },
                  ]}
                >
                  {convertESTtoUserLocalDateAndTime(
                    testingValue.testingValue,
                    "MMM DD, YYYY hh:mm a",
                    "MM/DD/YYYYTHH:mm:s"
                  )}
                  {testingValue.winner && (
                    <Tooltip
                      title={`Winner declared on ${convertESTtoUserLocalDateAndTime(
                        testingValue.winnerDeclarationTime
                      )} by system.`}
                      arrow
                    >
                      <SvgIcon sx={{ ml: 1 }}>
                        <EmojiEventsIcon htmlColor="#ffc64c" />
                      </SvgIcon>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={`This campaign was selected for ${
                      props?.campaignData?.abTestInfo?.winingExecutionType
                    } execution on ${convertESTtoUserLocalDateAndTime(
                      testingValue.mainCampaignSentTime
                    )}`}
                    arrow
                  >
                    {testingValue.usedForCampaign ? (
                      <SvgIcon sx={{ ml: 1 }}>
                        <CheckCircleIcon htmlColor="#008000" />
                      </SvgIcon>
                    ) : (
                      <></>
                    )}
                  </Tooltip>
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  sx={[
                    {
                      display: "flex",
                      alignItems: "center",
                    },
                    clickable && {
                      cursor: "pointer",
                    },
                  ]}
                >
                  {props.campaignData?.abTestInfo?.testingVariable === "content"
                    ? `Variant ${index + 1}`
                    : testingValue.testingValue}{" "}
                  {testingValue.winner && (
                    <Tooltip
                      title={`Winner declared on ${convertESTtoUserLocalDateAndTime(
                        testingValue.winnerDeclarationTime
                      )} by system.`}
                      arrow
                    >
                      <SvgIcon sx={{ ml: 1 }}>
                        <EmojiEventsIcon htmlColor="#ffc64c" />
                      </SvgIcon>
                    </Tooltip>
                  )}
                  {testingValue.usedForCampaign && (
                    <Tooltip
                      title={`This campaign was selected for manual execution on ${convertESTtoUserLocalDateAndTime(
                        testingValue.mainCampaignSentTime
                      )}`}
                      arrow
                    >
                      <SvgIcon sx={{ ml: 1 }}>
                        <CheckCircleIcon htmlColor="#008000" />
                      </SvgIcon>
                    </Tooltip>
                  )}
                </Typography>
              )}
            </Stack>
          ),
          status: isTruthy(status!) ? (
            <Chip
              label={status}
              sx={{
                color: getStatusColor(status!),
                borderRadius: "5px",
                minWidth: "90px",
              }}
            />
          ) : (
            <Skeleton animation="wave" variant="rounded" height={25} />
          ),
          requested: (
            <>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                }}
              >
                {getFormattedStatsCount(requestedCount)}
              </Typography>
            </>
          ),
          success: (
            <>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                }}
              >
                {(
                  (parseInt(successCount) /
                    parseInt(requestedCount == "0" ? "1" : requestedCount)) *
                  100
                ).toFixed()}
                %
              </Typography>
            </>
          ),
          opens: (
            <>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                }}
              >
                {(
                  (parseInt(openedCount) /
                    parseInt(successCount == "0" ? "1" : successCount)) *
                  100
                ).toFixed()}
                %
              </Typography>
            </>
          ),
          clicks: (
            <>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                }}
              >
                {(
                  (parseInt(clickedCount) /
                    parseInt(successCount == "0" ? "1" : successCount)) *
                  100
                ).toFixed()}
                %
              </Typography>
            </>
          ),
        };
      }
    );
  };

  const getMaxScheduleTime = () => {
    if (!props.childCampaign || props.childCampaign.length === 0) return null;

    let maxScheduleTime = "";

    if (props.campaignData?.abTestInfo?.testingVariable === "scheduleTime") {
      const times = props.campaignData?.abTestInfo?.abTestResults.map(
        (campaign: AbTestResult) => moment(campaign.testingValue)
      );

      maxScheduleTime = moment(
        Math.max(...times.map((time: any) => time.valueOf()))
      ).format("MMM DD, YYYY hh:mm a");
    } else {
      const times = props.childCampaign?.map((campaign: CampaignOverviewType) =>
        moment(campaign?.campaign?.scheduleTime)
      );
      maxScheduleTime = moment(
        Math.max(...times.map((time: Moment) => time.valueOf()))
      ).format("MMM DD, YYYY hh:mm a");
    }

    return convertESTtoUserLocalDateAndTime(
      maxScheduleTime,
      "MMM DD, YYYY hh:mm a",
      "MMM DD, YYYY hh:mm a"
    );
  };

  const getConfigDetails = () => {
    return (
      <>
        <Typography
          gutterBottom
          variant="h6"
          sx={{ color: theme.palette.primary.dark }}
          mb={2}
        >
          Test Configuration
        </Typography>
        <CenteredCardLayout removeLastChildBottomPadding>
          <Box p={2}>
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1">Testing variable</Typography>
                    </Stack>
                    <Typography fontWeight={700}>
                      {
                        variables[
                          props.campaignData?.abTestInfo?.testingVariable!
                        ]?.label
                      }
                    </Typography>
                  </Stack>
                  <Divider flexItem sx={{ mt: 1 }} />
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1">Winning metric</Typography>
                    </Stack>
                    <Typography fontWeight={700}>
                      By{" "}
                      {
                        winningMetric[
                          props.campaignData?.abTestInfo?.winingMetric!
                        ].short
                      }
                    </Typography>
                  </Stack>
                  <Divider flexItem sx={{ mt: 1 }} />
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1">
                        Results declaration
                      </Typography>
                    </Stack>
                    <Typography fontWeight={700}>
                      {getABResultAfterType(
                        props.campaignData?.abTestInfo?.resultsAfterInterval!,
                        props.campaignData?.abTestInfo?.resultsAfterType ??
                          "".toLowerCase()!
                      )}{" "}
                      (~{" "}
                      {getTentativeResultTime(
                        getMaxScheduleTime(),
                        props.campaignData?.abTestInfo?.resultsAfterInterval!,
                        props.campaignData?.abTestInfo?.resultsAfterType.toLowerCase()!
                      ) ?? ""}
                      )
                    </Typography>
                  </Stack>
                  <Divider flexItem sx={{ mt: 1 }} />
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1">Testing ratio</Typography>
                    </Stack>
                    <Typography fontWeight={700}>
                      {props.campaignData?.abTestInfo?.ratioForTesting}%
                    </Typography>
                  </Stack>
                  <Divider flexItem sx={{ mt: 1 }} />
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1">Execution</Typography>
                    </Stack>
                    <Typography fontWeight={700}>
                      {
                        deliveryOptions[
                          props.campaignData?.abTestInfo?.winingExecutionType!
                        ].label
                      }
                    </Typography>
                  </Stack>
                  <Divider flexItem sx={{ mt: 1 }} />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </CenteredCardLayout>
      </>
    );
  };

  const getCombinationsDetails = () => {
    return (
      <>
        <Stack
          direction="row"
          mb={2}
          mt={4}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            gutterBottom
            variant="h6"
            sx={{ color: theme.palette.primary.dark }}
          >
            Combinations
          </Typography>
          {selectedCampaign && (
            <Button variant="contained" onClick={() => setSchedule(true)}>
              Send
            </Button>
          )}
        </Stack>
        <Stack spacing={1}>
          <Card>
            <CustomTable
              isLoading={!isTruthy(props.campaignData)}
              headers={tableHeader}
              rows={getTableRows()}
              showPagination={false}
            />
          </Card>
          <Typography
            variant="caption"
            fontStyle="italic"
            color="text.secondary"
          >
            After the results are announced, the table above will display a
            snapshot of the stats at the moment the winner is declared. Click on
            the variable name to view the real-time stats for the variant
            campaign.
          </Typography>
        </Stack>
      </>
    );
  };

  return (
    <>
      {getConfigDetails()}
      {getCombinationsDetails()}
      <SendABCampaign
        handleDialogClose={() => setSchedule(false)}
        isDialogOpen={schedule}
        selectedCampaignData={selectedCampaign}
      />
    </>
  );
};

export default ABTestDetails;
